import { IBaseTreeNode, ITreeNode } from "./tree/interfaces"

export const getJsonValueByPath = <T>(json: T, path: string) =>
  path.split(".").reduce((accumulator, value) => accumulator[value], json)

// Linear versions (Set-based) of the former quadratic reduce/some/spread implementations; same result: first
// occurrence kept, original order preserved.
export const deleteDoublons = <T extends any[]>(arr: T): T => {
  const seen = new Set<any>()
  return arr.filter((value) => {
    if (seen.has(value)) {
      return false
    }
    seen.add(value)
    return true
  }) as T
}

export const deleteDoublonComplex = <T>(arr: T[], idPath: string) => {
  const seen = new Set<any>()
  return arr.filter((value) => {
    const id = getJsonValueByPath(value, idPath)
    if (seen.has(id)) {
      return false
    }
    seen.add(id)
    return true
  })
}

export const haveArraysTheSameOrder = (
  arr1: any[],
  arr2: any[],
  key: string
) => {
  if (arr1.length !== arr2.length) {
    return false
  }

  let result = true
  arr1.forEach((o, i) => (result = result && o[key] === arr2[i][key]))
  return result
}

interface IMergeArraysByIdArray {
  [x: string]: any
}

interface IMergeArrayByItemsIdOptions {
  key?: string | string[]
  addLeftOvers?: boolean
  removeMissing?: boolean | string
  calculateNumbers?: boolean
  stringList?: "merge" | "original" | "replace"
}

/**
 * Fonction that allows to merge arrays and merge items inside arrays based on a specific key
 * @param original Initial array that will be filled by  replace
 * @param replace Second array
 * @param options key = id, addLeftOvers = true
 */
export const mergeArrayByItemsId = <T extends IMergeArraysByIdArray>(
  original: T[],
  replace: any[],
  options: IMergeArrayByItemsIdOptions = {}
): T[] => {
  const {
    key = "id",
    addLeftOvers = true,
    calculateNumbers = false,
    removeMissing = "",
    stringList = "merge",
  } = options
  // ----------------------- SECURITY AND PERFORMANCE CHECKS -------------------
  if (original && replace) {
    if (original && original[0] && typeof original[0] === "string") {
      return [
        ...(stringList === "replace" ? [] : original),
        ...(stringList === "original" ? [] : replace),
      ]
    }
    // -------------------------- KEY SELECTION -------------------------------
    let usedKey = ""
    if (Array.isArray(key)) {
      let it = 0
      usedKey = key[it]
      while (original[0]?.[usedKey] === undefined) {
        it += 1
        usedKey = key[it]
        if (it > 10) {
          break
        }
      }
    } else {
      usedKey = key
    }

    // --------------------------------FOR EACH LOOP --------------------------
    const merged: T[] = []
    original.forEach((originalItem) => {
      // ---------------------- SEARCH FOR REPLACEITEM ------------------------------
      const replaceItem = replace.find((itmInner) => {
        return (
          JSON.stringify(itmInner[usedKey]) ===
          JSON.stringify(originalItem[usedKey])
        )
      })
      if (!replaceItem) {
        if (removeMissing) {
          if (typeof removeMissing !== "string") {
            return
          }
        }
      }

      // ---------------------- NEW VALUE FUNCTION ------------------------------
      const getNewValue = (k: string) => {
        let nV
        if (Array.isArray(originalItem[k])) {
          nV = mergeArrayByItemsId(originalItem[k], replaceItem[k], {
            ...options,
            removeMissing:
              (removeMissing &&
                typeof removeMissing === "string" &&
                removeMissing === k) ||
              removeMissing,
          })
        } else if (replaceItem[k] !== undefined) {
          if (
            calculateNumbers &&
            typeof replaceItem[k] === "number" &&
            typeof originalItem[k] === "number"
          ) {
            nV = replaceItem[k] + originalItem[k]
          } else {
            nV = replaceItem[k]
          }
        } else {
          nV = originalItem[k]
        }
        return nV
      }

      // ---------------------- NEW ITEM CREATION ------------------------------
      let newItem
      if (
        !calculateNumbers &&
        JSON.stringify(replaceItem) === JSON.stringify(originalItem)
      ) {
        newItem = { ...originalItem }
      } else {
        newItem =
          (replaceItem &&
            Object.keys(originalItem).reduce((acc, k) => {
              return {
                ...acc,
                [k]: getNewValue(k),
              }
            }, {} as T)) ||
          originalItem
      }

      // const newItem = {
      //   ...originalItem,
      //   ...(replaceItem || {})
      // }

      merged.push(newItem)
      replace = replace.filter((m) =>
        replaceItem
          ? JSON.stringify(m[usedKey]) !== JSON.stringify(replaceItem[usedKey])
          : true
      )
    })
    return [...merged, ...(addLeftOvers ? replace : [])]
  }
  return original
}

let test = 0
type IApplyToTreeCallback<T extends IBaseTreeNode<T>> = (node: T) => T
export const applyCallbackToTreeNodes = <T extends IBaseTreeNode<T>>(
  tree: T[],
  callback: IApplyToTreeCallback<T>
): T[] => {
  test = test + 1
  return tree.reduce((acc, node) => {
    return [
      ...acc,
      {
        ...callback(node),
        children:
          node.children && applyCallbackToTreeNodes(node.children, callback),
      },
    ]
  }, [] as T[])
}
